import React from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {useForm} from 'react-hook-form'
import {Box, Button, Heading, Link, Text} from '@chakra-ui/react'

import Field from '../../components/field'
import HeaderContent from '../../components/header-content'
import Seo from '../../components/seo'
import {API_ERROR_MESSAGE} from '../../constants'
import useMultiSite from '../../hooks/use-multi-site'
import {useCustomerServiceDeclarationOfWithdrawalMutation} from '../../hooks/useScapiCustomApi'
import {useToast} from '../../hooks/use-toast'
import {encodeUrlLocale} from '../../utils/locale'
import useWithdrawalFields from './use-withdrawal-fields'

const RETURN_PORTAL_URL = '/return-order'

const getWithdrawalErrorMessage = async (error, fallbackMessage) => {
    try {
        const errorData = await error?.response?.json?.()
        return errorData?.detail || fallbackMessage
    } catch {
        return fallbackMessage
    }
}

const Withdrawal = () => {
    const {formatMessage} = useIntl()
    const {site, locale} = useMultiSite()
    const toast = useToast()
    const form = useForm({mode: 'onSubmit'})
    const fields = useWithdrawalFields({form})
    const returnPortalUrl = `/${site?.alias || site?.id}/${encodeUrlLocale(
        locale?.alias || locale?.id
    )}${RETURN_PORTAL_URL}`
    const {mutateAsync: postDeclarationOfWithdrawal, isLoading: isDeclarationOfWithdrawalLoading} =
        useCustomerServiceDeclarationOfWithdrawalMutation({
            onError: async (error) => {
                console.error('Error while sending declaration of withdrawal form', error)
                const fallbackMessage = formatMessage(API_ERROR_MESSAGE)
                toast({
                    title: await getWithdrawalErrorMessage(error, fallbackMessage),
                    status: 'error'
                })
            },
            onSuccess: () => {
                toast({
                    title: formatMessage({
                        defaultMessage: 'The declaration of withdrawal has been sent successfully.',
                        id: 'withdrawal.toast.success'
                    }),
                    status: 'success'
                })
                form.reset()
            }
        })

    const submitForm = async (data) => {
        try {
            await postDeclarationOfWithdrawal({
                body: JSON.stringify({
                    firstName: data.firstName,
                    lastName: data.lastName,
                    email: data.email,
                    orderNumber: data.orderNumber,
                    locale: locale.id
                })
            })
        } catch {
            // The mutation onError handler owns user-facing feedback.
        }
    }

    const isLoading = form.formState.isSubmitting || isDeclarationOfWithdrawalLoading

    return (
        <>
            <HeaderContent />
            <Box
                data-testid="withdrawal-page"
                bg="background.section.subtle"
                minHeight="60vh"
                py={[8, 16]}
            >
                <Box
                    bg="white"
                    borderRadius="base"
                    display="flex"
                    flexDirection="column"
                    gap={8}
                    mx="auto"
                    maxWidth="560px"
                    p={[6, 10]}
                    width="calc(100% - 32px)"
                >
                    <Seo
                        title={formatMessage({
                            defaultMessage: 'Declaration of Withdrawal',
                            id: 'withdrawal.page.title'
                        })}
                        description={formatMessage({
                            defaultMessage:
                                'You can use this form to withdraw from your contract. Please note that your declaration of withdrawal will only be effective if you are actually entitled to a right of withdrawal.',
                            id: 'withdrawal.page.description'
                        })}
                    />
                    <Box display="flex" flexDirection="column" gap={4}>
                        <Heading as="h1" color="stokkePink.dark" textStyle="Regular 45XLarge">
                            <FormattedMessage
                                defaultMessage="Declaration of Withdrawal"
                                id="withdrawal.page.title"
                            />
                        </Heading>
                        <Text textStyle="Regular Medium">
                            <FormattedMessage
                                defaultMessage="You can use this form to withdraw from your contract. Please note that your declaration of withdrawal will only be effective if you are actually entitled to a right of withdrawal."
                                id="withdrawal.page.description"
                            />
                        </Text>
                    </Box>
                    <Box
                        as="form"
                        display="flex"
                        flexDirection="column"
                        gap={6}
                        onSubmit={form.handleSubmit(submitForm)}
                        noValidate
                    >
                        <Box display="flex" flexDirection="column" gap={5}>
                            <Field {...fields.firstName} />
                            <Field {...fields.lastName} />
                            <Field {...fields.email} />
                            <Field {...fields.orderNumber} />
                        </Box>
                        <Box display="flex" flexDirection="column" alignItems="center" gap={4}>
                            <Button type="submit" isLoading={isLoading}>
                                <FormattedMessage defaultMessage="Submit" id="global.submit" />
                            </Button>
                            <Link
                                href={returnPortalUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                color="stokkeCore.black"
                                fontWeight="semibold"
                                textDecoration="underline"
                                textUnderlineOffset="3px"
                            >
                                <FormattedMessage
                                    defaultMessage="Return your products now"
                                    id="withdrawal.link.return_portal"
                                />
                            </Link>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </>
    )
}

Withdrawal.getTemplateName = () => 'withdrawal'

export default Withdrawal
